// React
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// GraphQL
import { gql, useLazyQuery } from "@apollo/client";

// Material-UI components
import AccordionDetails from "@material-ui/core/AccordionDetails";

// Icons and Helpers
import binIcon from "../../../../../assets/icons/bin.svg";
import { getIntials, getName } from "../../../../../helper";

// Custom Hooks
import useOnClickOutside from "../../../../../hooks/useOnClickOutside";

// Custom Components
import ProfileWithName from "../../../../ListView/ListViewRows/ProfileWithName/ProfileWIthName";

// Constants
import { UPDATE_LEVEL } from "../../../CreateNewFolder";

const GET_USER_BY_NAME = gql`
  query userSearch($name: String!, $includeAdmin: Boolean) {
    userSearch(name: $name, includeAdmin: $includeAdmin) {
      id
      username
      firstName
      lastName
      email
      profile {
        id
        image
        role {
          id
          name
        }
      }
    }
  }
`;

function UserSearchView({ approvalLevel, mutationHandler, accessUsers = [] }) {
  // APIs
  const [
    getUserByName,
    { data: { userSearch: currentSearchedUser = [] } = {} },
  ] = useLazyQuery(GET_USER_BY_NAME);
  // Local states
  const [showUserSearchDropdown, setShowUserSearchDropdown] = useState(false);
  const [searchUserText, setSearchUserText] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
  });
  const userSearchDropdownRef = useRef();

  // Local variable
  let selectedUser = [];
  let selectedUserIds = [];
  let accessUserIds = [];
  if (Array.isArray(approvalLevel.users)) {
    selectedUser = approvalLevel.users;
    selectedUserIds = approvalLevel.users.map((user) => user.id);
  }
  if (Array.isArray(accessUsers)) {
    accessUserIds = accessUsers.map((user) => user.id);
  }
  const filteredCurrentSearchedUser = currentSearchedUser.filter(
    (user) =>
      (!selectedUserIds.includes(user.id) && accessUserIds.includes(user.id)) ||
      isITAdminOrAdmin(user)
  );

  // handlers
  const searchUserOnChangeHandler = (e) => {
    setSearchUserText(e.target.value);
  };

  // Effects
  // Fetch the user by searched text
  useEffect(() => {
    if (searchUserText === "") {
      setShowUserSearchDropdown(false);
      return;
    }
    setShowUserSearchDropdown(true);
    getUserByName({
      variables: { name: searchUserText, includeAdmin: true },
    });
  }, [getUserByName, searchUserText, setShowUserSearchDropdown]);

  // Positioning the user search options
  useEffect(() => {
    const dropdownRef = userSearchDropdownRef.current;

    if (!dropdownRef || !showUserSearchDropdown) {
      return;
    }

    const inputRect = dropdownRef.getBoundingClientRect();
    const inputTop = inputRect.top + window.scrollY;
    const inputLeft = inputRect.left + window.scrollX;

    const dropdownHeight = dropdownRef.offsetHeight;
    const dropdownWidth = dropdownRef.offsetWidth;

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // Ensure dropdown fits within the viewport vertically
    const top =
      inputTop + inputRect.height + dropdownHeight <= windowHeight
        ? inputTop + inputRect.height
        : windowHeight - dropdownHeight;

    // Ensure dropdown fits within the viewport horizontally
    const left =
      inputLeft + dropdownWidth <= windowWidth
        ? inputLeft
        : windowWidth - dropdownWidth;

    setDropdownPosition({
      top,
      left,
    });
  }, [showUserSearchDropdown, userSearchDropdownRef]);

  // Listeners

  // Hide the user search options
  useOnClickOutside(
    userSearchDropdownRef,
    () => showUserSearchDropdown && setShowUserSearchDropdown(false)
  );

  return (
    <AccordionDetails className="pt-0">
      <div className="d-flex flex-column ml-10">
        <input
          type="text"
          maxLength="50"
          placeholder="&#xF002; Search"
          className="search-placeholder"
          value={searchUserText}
          onChange={searchUserOnChangeHandler}
        />
        {showUserSearchDropdown && (
          <div
            className={`approval-search-dropdown`}
            ref={userSearchDropdownRef}
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            {filteredCurrentSearchedUser.length > 0 &&
              filteredCurrentSearchedUser
                .filter((user) => !selectedUserIds.includes(user.id))
                .map((user, index) => {
                  return (
                    <div
                      className={`approval-search-dropdown-row ${
                        filteredCurrentSearchedUser.length - 1 !== index
                          ? "add-shadow"
                          : ""
                      }`}
                      key={user.id}
                      onClick={() => {
                        const payload = {
                          id: approvalLevel.id,
                          data: {
                            users: [
                              ...approvalLevel.users,
                              {
                                ...user,
                                id: user.id,
                                name: getName(user),
                                avatarUrl: user.profile.image,
                                nameIntial: getIntials(user),
                              },
                            ],
                          },
                        };
                        mutationHandler(UPDATE_LEVEL, payload);
                        setSearchUserText("");
                      }}
                    >
                      <ProfileWithName
                        sKey={user.id}
                        nameIntial={getIntials(user)}
                        personName={getName(user)}
                        profile={user.profile.image}
                        isPopover={false}
                        isPointerCursor={true}
                      />
                    </div>
                  );
                })}
            {filteredCurrentSearchedUser.length === 0 && (
              <p className="text-center mb-0">No matched result found.</p>
            )}
          </div>
        )}
        {selectedUser.map((user) => {
          return (
            <div className="d-flex ml-1 mt-5 user-container">
              <section className="d-flex justify-content-between w-100">
                <div>
                  <ProfileWithName
                    sKey={user.id}
                    nameIntial={user.nameIntial}
                    personName={user.name}
                    profile={user.avatarUrl}
                    isPopover={false}
                  />
                </div>

                <img
                  src={binIcon}
                  alt="x"
                  className="img-fluid cursor-pointer"
                  onClick={() => {
                    const payload = {
                      id: approvalLevel.id,
                      data: {
                        users: approvalLevel.users.filter(
                          (u) => u.id !== user.id
                        ),
                      },
                    };
                    mutationHandler(UPDATE_LEVEL, payload);
                  }}
                />
              </section>
            </div>
          );
        })}
      </div>
    </AccordionDetails>
  );
}
UserSearchView.propTypes = {
  approvalLevel: PropTypes.object.isRequired,
  mutationHandler: PropTypes.func.isRequired,
  accessUsers: PropTypes.arrayOf(PropTypes.object),
};
export default UserSearchView;

function isITAdminOrAdmin(userDetails) {
  try {
    if (!userDetails || !userDetails.profile || !userDetails.profile.role) {
      throw new Error("Invalid user details. Missing required data.");
    }

    const roleName = userDetails.profile.role.name;

    return roleName === "IT Admin" || roleName === "Admin";
  } catch (error) {
    console.error("Error:", error.message);
    return false;
  }
}
